import { Volume } from '~/shared/types/api';
import { toaster } from '~/shared/utils/toaster';
import { DatafySwitchContextType } from '~/shared/context/DatafySwitchProvider';
import {
  canBeDatafied,
  canBeUnDatafied,
  isSwitchedOn,
  isSwitchDisabled,
} from '~/shared/utils/volumeHelpers';

export const baseUrl = `/api/v1/volumes`;

export function buildVolumePageUrl(volumeId: string, accountId?: string): string {
  const path = `/fleet-manager/${volumeId}`;
  if (!accountId) {
    return path;
  }
  return `${path}?${new URLSearchParams({ accountId }).toString()}`;
}

export { canBeDatafied, canBeUnDatafied, isSwitchedOn, isSwitchDisabled };

export function datafyErrorToast(
  volumeId: Volume['volumeId'],
  event: DatafySwitchContextType['datafySwitchVolumes'][number]['state']
) {
  return toaster({
    variant: 'error',
    title: `${event === 'datafying' ? 'Datafication' : 'Undatafication'} Error`,
    message: `Volume ${volumeId} has failed to ${event === 'datafying' ? 'Datafy' : 'Undatafy'}.`,
    toastId: volumeId,
    volumeId,
  });
}

export const optmisticSwitchingInterval = 20000;

export const isVolumeExcludedFromAutodatafy = (volume: Volume): boolean => {
  return volume.excludeFromAutoDatafy === true;
};

// Order matters: first match wins (same priority as the legacy AutoScaleRenderer if-chain).
// Reasons with no rule (e.g. "Needs Reboot", "Boot Volume") pass through unchanged.
const NOT_DATAFIABLE_REASON_LABELS: { match: (reason: string) => boolean; label: string }[] = [
  {
    match: (reason) => reason.includes('no autoscaler') || reason.includes('unmonitored'),
    label: 'No AutoScaler',
  },
  { match: (reason) => reason.includes('unattached'), label: 'Unattached' },
  {
    match: (reason) => reason.includes('unsupported os') || reason.includes('os'),
    label: 'Unsupported OS',
  },
  {
    match: (reason) =>
      reason.includes('unsupported fs') ||
      reason.includes('filesystem') ||
      reason.includes('fs'),
    label: 'Unsupported FS',
  },
  {
    match: (reason) =>
      reason.includes('unsupported volume type') || reason.includes('volume type'),
    label: 'Unsupported Volume Type',
  },
];

export function formatNotDatafiableReason(reason: string): string {
  const normalized = reason.toLowerCase();
  return NOT_DATAFIABLE_REASON_LABELS.find((rule) => rule.match(normalized))?.label ?? reason;
}
